/**
 * HostServices — the bundle of host-side registries that make the four
 * "host-effect" ActionFlow actions functional, plus `wireHostHooks` which binds
 * them to a runtime-v2 `ActionFlowEngine`.
 *
 * The engine exposes four nullable hooks (headless no-op by default):
 *   onEmitEvent     → AppEventBus.emit            (global, by event name)
 *   onOpenModal     → ModalStore.open             (global, by modal id)
 *   onInvokeCallback→ CallbackRegistry.invoke      (block-scoped, by name)
 *   onMediaControl  → MediaRegistry.control        (block-scoped, by command)
 *
 * `invokeCallback` and `mediaControl` are block-instance-scoped, but the engine's
 * hook signatures don't carry a block id (a global hook would shadow `onApprove`
 * across cards / command the wrong `<video>`). So the host supplies a
 * `getActiveBlockId()` resolver — AppRuntime sets the dispatching block's
 * namespace synchronously before `executeFlow`, and these fire-and-forget hooks
 * read it at call time.
 */

import {
  ACTION_FLOW_BLOCK_NAMESPACE_CONTEXT_KEY,
  type ActionFlowEngine,
} from "@cogo/runtime-v2"
import { AppEventBus } from "./event-bus"
import { CallbackRegistry } from "./callback-registry"
import { MediaRegistry } from "./media-registry"
import { MotionRegistry } from "./motion-registry"
import { ModalStore } from "./modal-host"

export interface HostServices {
  eventBus: AppEventBus
  modalStore: ModalStore
  callbackRegistry: CallbackRegistry
  mediaRegistry: MediaRegistry
  motionRegistry: MotionRegistry
}

export function createHostServices(): HostServices {
  return {
    eventBus: new AppEventBus(),
    modalStore: new ModalStore(),
    callbackRegistry: new CallbackRegistry(),
    mediaRegistry: new MediaRegistry(),
    motionRegistry: new MotionRegistry(),
  }
}

/**
 * Bind the engine's four host hooks to the services. Additive + idempotent —
 * assigning the public hook fields; existing null-default behavior is unchanged
 * for any engine this isn't called on.
 *
 * @param getActiveBlockId resolves the block whose flow is currently dispatching
 *   (for block-scoped callback/media). Returns null when not inside a dispatch.
 */
export function wireHostHooks(
  engine: ActionFlowEngine,
  services: HostServices,
  getActiveBlockId: () => string | null = () => null,
): void {
  const resolveBlockId = (context?: Record<string, unknown>) => {
    const scoped = context?.[ACTION_FLOW_BLOCK_NAMESPACE_CONTEXT_KEY]
    return typeof scoped === "string" && scoped.length > 0 ? scoped : getActiveBlockId()
  }
  engine.onEmitEvent = (name, payload) => services.eventBus.emit(name, payload)
  engine.onOpenModal = (modalId, props) => services.modalStore.open(modalId, props)
  engine.onInvokeCallback = (name, args, context) => services.callbackRegistry.invoke(resolveBlockId(context), name, args)
  engine.onMediaControl = (command, args, context) => services.mediaRegistry.control(resolveBlockId(context), command, args)
  engine.onRunMotion = (params, context) => services.motionRegistry.run(resolveBlockId(context), params)
}
